import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  postRequestUnAuthenticated,
  postRequestAuthenticated,
  postRequestAuthenticatedWithFile,
} from "./../../services/rest";
import { createTopic, updateTopic } from "./createTopicSlice";
import { updateTopicsOrder, removeMember } from "./reorderTopicSlice";
import { deleteTopic } from "./deleteTopicSlice";
import { joinChannel, leaveChannel } from "./channelSlice";
import { deleteChannel } from "./deleteChannelSlice";

export const createChannel = createAsyncThunk(
  "channel/create-channel",
  async (channelData, { rejectWithValue }) => {
    try {
      const response = await postRequestAuthenticatedWithFile(
        "/create/channel",
        channelData
      );
      // The backend returns success:true with limitReached:true (and no
      // channel) when the plan's channel cap is hit — surface that message
      // instead of letting the modal choke on a missing channel id.
      if (response.success && response.channel) {
        return response.channel;
      } else {
        return rejectWithValue(response.message || "Could not create channel.");
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
export const updateChannel = createAsyncThunk(
  "channel/update-channel",
  async (channelData, { rejectWithValue }) => {
    try {
      const response = await postRequestAuthenticatedWithFile(
        "/update/channel",
        channelData
      );
      if (response.success) {
        return response.channel;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
export const createGeneralTopic = createAsyncThunk(
  "channel/create-general-topic",
  async (channelId, { rejectWithValue }) => {
    try {
      const response = await postRequestAuthenticated("/create/general/topic", {
        channelId,
      });
      if (response.success) {
        return response.topic;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
export const fetchMyChannels = createAsyncThunk(
  "channel/fetch-my-channels",
  async (_, { rejectWithValue }) => {
    try {
      const response = await postRequestAuthenticated("/fetch/my/channels");
      if (response.success) {
        return response.channels;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
export const fetchChannels = createAsyncThunk(
  "channel/fetch-channels",
  async (username, { rejectWithValue }) => {
    try {
      const response = await postRequestUnAuthenticated("/fetch/channels", {
        username,
      });
      if (response.success) {
        return response.channels;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
// export const fetchEmbedChannels = createAsyncThunk(
//   "channel/fetch-embed-channels",
//   async (data, { rejectWithValue }) => {
//     try {
//       const response = await postRequestAuthenticated(
//         "/fetch/embed/channels",
//         data
//       );
//       if (response.success) {
//         return response.channels;
//       } else {
//         return rejectWithValue(response.message);
//       }
//     } catch (error) {
//       return rejectWithValue(error.message);
//     }
//   }
// );
// export const fetchUserChannels = createAsyncThunk(
//   "channel/fetch-user-channels",
//   async (username, { rejectWithValue }) => {
//     try {
//       const response = await postRequestUnAuthenticated(
//         "/fetch/user/channels",
//         { username }
//       );
//       if (response.success) {
//         return response.channels;
//       } else {
//         return rejectWithValue(response.message);
//       }
//     } catch (error) {
//       return rejectWithValue(error.message);
//     }
//   }
// );
export const fetchCommunityChannel = createAsyncThunk(
  "channel/fetch-community-channels",
  async (_, { rejectWithValue }) => {
    try {
      const response = await postRequestUnAuthenticated(
        "/fetch/community/channel"
      );
      if (response.success) {
        return response.channel;
      } else {
        return rejectWithValue(response.message);
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const channelItemsSlice = createSlice({
  name: "channelItems",
  initialState: {
    channels: [],
    userChannels: [],
    selectedChannel: null,
    channelstatus: "idle",
    topicstatus: "idle",
    selectedPage: null,
    loading: false,
    error: null,
    communityChannel: null,
  },
  reducers: {
    setSelectedChannel: (state, action) => {
      state.selectedChannel = action.payload;
    },
    setSelectedPage: (state, action) => {
      state.selectedPage = action.payload;
    },
    clearchannels: (state) => {
      state.channels = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createGeneralTopic.pending, (state) => {
        state.channelstatus = "loading";
      })
      .addCase(createGeneralTopic.fulfilled, (state, action) => {
        state.channelstatus = "idle";
        const topic = action.payload;
        let index = state.channels.findIndex(
          (item) => item._id === topic.channel
        );
        if (index !== -1) {
          state.channels[index].topics.unshift(topic);
        }
      })
      .addCase(createGeneralTopic.rejected, (state, action) => {
        state.channelstatus = "idle";
        state.channelNameError = action.payload || action.error.message;
      })
      .addCase(createChannel.pending, (state) => {
        state.channelstatus = "loading";
      })
      .addCase(createChannel.fulfilled, (state, action) => {
        state.channelstatus = "idle";
        state.channels.unshift(action.payload);
        // The admin ChannelsPanel lists `userChannels`, so add the new channel
        // there too (mirroring join/update) — otherwise it doesn't appear until
        // a manual refresh / window-focus refetch.
        if (!state.userChannels.some((c) => c._id === action.payload._id)) {
          state.userChannels.unshift(action.payload);
        }
      })
      .addCase(createChannel.rejected, (state, action) => {
        state.channelstatus = "idle";
        state.channelNameError = action.payload || action.error.message;
      })
      .addCase(updateChannel.pending, (state) => {
        state.channelstatus = "loading";
      })
      .addCase(updateChannel.fulfilled, (state, action) => {
        state.channelstatus = "idle";
        const channel = action.payload;
        let index = state.channels.findIndex(
          (item) => item._id === channel._id
        );
        if (index !== -1) {
          state.channels[index] = channel;
        }
        let index2 = state.userChannels.findIndex(
          (item) => item._id === channel._id
        );
        if (index2 !== -1) {
          state.userChannels[index2] = channel;
        }
      })
      .addCase(updateChannel.rejected, (state, action) => {
        state.channelstatus = "idle";
        state.channelNameError = action.payload || action.error.message;
      })

      .addCase(joinChannel.fulfilled, (state, action) => {
        state.channelstatus = "idle";
        const r = action.payload || {};
        // On a real join, add the channel to the joined list (userChannels).
        if (r.joined && r.channel?._id) {
          if (!state.userChannels.some((c) => c._id === r.channel._id)) {
            state.userChannels.unshift(r.channel);
          }
        }
      })
      .addCase(leaveChannel.fulfilled, (state, action) => {
        state.channelstatus = "idle";
        const channelId = action.payload?._id;
        if (channelId) {
          // left the channel -> drop it from the joined lists
          state.userChannels = state.userChannels.filter(
            (c) => c._id !== channelId
          );
          state.channels = state.channels.filter((c) => c._id !== channelId);
        }
      })
      .addCase(deleteChannel.fulfilled, (state, action) => {
        // action.payload is the deleted channelId
        const channelId = action.payload;
        state.userChannels = state.userChannels.filter(
          (c) => c._id !== channelId
        );
        state.channels = state.channels.filter((c) => c._id !== channelId);
      })
      .addCase(fetchMyChannels.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMyChannels.fulfilled, (state, action) => {
        state.loading = false;
        state.userChannels = action.payload;
      })
      .addCase(fetchMyChannels.rejected, (state, action) => {
        state.loading = false;
        state.channelNameError = action.payload || action.error.message;
      })
      .addCase(fetchChannels.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchChannels.fulfilled, (state, action) => {
        state.loading = false;
        state.channels = action.payload;
      })

      // .addCase(fetchEmbedChannels.pending, (state) => {
      //   state.loading = true;
      // })
      // .addCase(fetchEmbedChannels.fulfilled, (state, action) => {
      //   state.loading = false;
      //   state.channels = action.payload;
      // })
      // .addCase(fetchEmbedChannels.rejected, (state, action) => {
      //   state.loading = false;
      //   state.channelNameError = action.payload || action.error.message;
      // })
      // .addCase(fetchUserChannels.pending, (state) => {
      //   state.loading = true;
      // })
      // .addCase(fetchUserChannels.fulfilled, (state, action) => {
      //   state.loading = false;
      //   state.userChannels = action.payload;
      // })
      // .addCase(fetchUserChannels.rejected, (state, action) => {
      //   state.loading = false;
      //   state.channelNameError = action.payload || action.error.message;
      // })
      .addCase(fetchCommunityChannel.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCommunityChannel.fulfilled, (state, action) => {
        state.loading = false;
        state.communityChannel = action.payload;
      })
      .addCase(fetchCommunityChannel.rejected, (state, action) => {
        state.loading = false;
        state.channelNameError = action.payload || action.error.message;
      })
      .addCase(createTopic.pending, (state) => {
        state.topicstatus = "loading";
      })
      .addCase(updateTopicsOrder.fulfilled, (state, action) => {
        state.status = "idle";
        const topicData = action.payload;
        const channelId = topicData.channelId;
        const topics = topicData.topics;
        let index = state.channels.findIndex((item) => item._id === channelId);
        if (index !== -1) {
          state.channels[index].topics = topics;
        }
      })
      .addCase(createTopic.fulfilled, (state, action) => {
        state.topicstatus = "idle";
        const topic = action.payload;
        let index = state.channels.findIndex(
          (item) => item._id === topic.channel
        );
        if (index !== -1) {
          state.channels[index].topics.push(topic);
        }
      })
      .addCase(updateTopic.fulfilled, (state, action) => {
        state.topicstatus = "idle";
        const topic = action.payload;
        let index = state.channels.findIndex(
          (item) => item._id === topic.channel
        );
        let topicIndex;
        if (index !== -1) {
          topicIndex = state.channels[index].topics.findIndex(
            (item) => item._id === topic._id
          );
          state.channels[index].topics[topicIndex].name = topic.name;
        }
      })
      .addCase(deleteTopic.fulfilled, (state, action) => {
        state.topicstatus = "idle";
        const topic = action.payload;
        let index = state.channels.findIndex(
          (item) => item._id === topic.channel
        );
        let topicIndex;
        if (index !== -1) {
          topicIndex = state.channels[index].topics.findIndex(
            (item) => item._id === topic._id
          );
          state.channels[index].topics.splice(topicIndex, 1);
        }
      })
      .addCase(removeMember.fulfilled, (state, action) => {
        state.status = "idle";
        const removeData = action.payload;
        let channelIndex = state.channels.findIndex(
          (channel) => channel._id === removeData.channelId
        );
        if (channelIndex !== -1) {
          let memberIndex = state.channels[channelIndex].members.findIndex(
            (member) => member === removeData.userId
          );
          if (memberIndex !== -1) {
            state.channels[channelIndex].members.splice(memberIndex, 1);
          }
        }
      })
      .addCase(createTopic.rejected, (state, action) => {
        state.topicstatus = "idle";
      });
  },
});

export const { setSelectedChannel, setSelectedPage, clearchannels } =
  channelItemsSlice.actions;

export default channelItemsSlice.reducer;
